import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import { Button, ButtonColor } from 'components/uikit/buttons/Button.tsx';
import { Field } from 'components/uikit/Field.tsx';
import { HookFormDropDownInput } from 'components/uikit/inputs/dropdown/HookFormDropDownInput.tsx';
import { CustomModal } from 'components/uikit/modal/CustomModal.tsx';
import { Loading } from 'components/uikit/suspense/Loading.tsx';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm.ts';
import { DocumentExportFormat } from 'services/api/api-client.types.ts';
import styles from './DownloadFilesModal.module.scss';
import dropdownStyles from 'components/uikit/DropdownSelector.module.scss';

type DownloadFilesModalProps = {
  onClose: () => void;
  isOpen: boolean;
  onDownload: (fileFormat: DocumentExportFormat) => Promise<void>;
};

export const DownloadFilesModal: React.FC<DownloadFilesModalProps> = (
  props,
) => {
  const i18n = useScopedTranslation(
    'Page.Projects.ProjectDetailsPage.FoldersTree.SelectModeOverlay.DownloadFilesModal',
  );
  const i18nEnums = useScopedTranslation('Enums.DocumentExportFormat');

  const form = useAdvancedForm<{ fileFormat: DocumentExportFormat }>(
    async (data) => {
      await props.onDownload(data.fileFormat);
      onModalClose();
    },
  );

  const onModalClose = () => {
    form.reset();
    form.clearErrors();
    props.onClose();
  };

  return (
    <CustomModal
      onClose={onModalClose}
      isOpen={props.isOpen}
      title={i18n.t('title')}
      isBlocking={true}
    >
      <Loading loading={form.formState.isSubmitting}>
        <form onSubmit={form.handleSubmitDefault}>
          <Field
            title={i18n.t('field_name')}
            className={styles.field}
            hint={{
              title: i18n.t('field_hint_title'),
              comment: i18n.t('field_hint_comment'),
              placement: 'top',
            }}
          >
            <HookFormDropDownInput
              control={form.control}
              name="fileFormat"
              options={[
                DocumentExportFormat.Pdf,
                DocumentExportFormat.OpenXml,
                DocumentExportFormat.Rtf,
                DocumentExportFormat.Dotx,
              ]}
              required={true}
              classes={dropdownClasses}
              getOptionLabel={(option) => i18nEnums.t(option)}
            />
          </Field>
          <div className={styles.footer}>
            <Button
              className={styles.button}
              color={ButtonColor.Secondary}
              title={i18n.t('cancel_button')}
              onClick={async () => onModalClose()}
            />
            <Button
              className={styles.button}
              color={ButtonColor.Default}
              type={'submit'}
              title={i18n.t('download_button')}
            />
          </div>
        </form>
      </Loading>
    </CustomModal>
  );
};

const dropdownClasses = {
  option: dropdownStyles.selectorOption,
  paper: dropdownStyles.selectorPaper,
  popper: dropdownStyles.selectorPopper,
  input: styles.input,
};
